import { ENDPOINT } from 'src/constants/api';

import { baseApi } from '../base';

const unwrapApiData = <T>(response: unknown): T => {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    (response as { data?: unknown }).data !== undefined
  ) {
    return (response as { data: T }).data;
  }

  return response as T;
};

export const STOCK_PHOTO_IMPORT_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  COMPLETED_WITH_ERRORS: 'completed_with_errors',
  FAILED: 'failed',
  CANCELED: 'canceled',
} as const;

export type StockPhotoImportBatchStatus =
  (typeof STOCK_PHOTO_IMPORT_STATUS)[keyof typeof STOCK_PHOTO_IMPORT_STATUS];

export type StockPhotoImportScope = 'account' | 'vehicle' | 'vehicles';

export type StockPhotoImportCounts = {
  queued: number;
  processing: number;
  imported: number;
  skipped: number;
  failed: number;
  canceled: number;
};

export type StockPhotoImportAccount = {
  id: number;
  name: string;
  avatar: string | null;
};

export type StockPhotoImportBatch = {
  id: string;
  operation_type: 'stock_photo_import';
  scope: StockPhotoImportScope;
  account_id: number;
  vehicle_id: number | null;
  status: StockPhotoImportBatchStatus;
  total: number;
  processed: number;
  progress: number;
  counts: StockPhotoImportCounts;
  imported_images: number;
  skipped_reasons: Record<string, number>;
  created_at: string;
  completed_at: string | null;
  canceled_at: string | null;
  status_url: string;
  account?: StockPhotoImportAccount;
};

export type CreateAccountStockPhotoImportParams = {
  accountId: number;
  idempotencyKey: string;
};

export type CreateVehiclesStockPhotoImportParams = {
  vehicleIds: number[];
  idempotencyKey: string;
};

export const stockPhotoImportBatchesPath = () =>
  `${ENDPOINT}/vehicles/import-stock-photos/batches`;

export const isActiveBatchStatus = (status: StockPhotoImportBatchStatus) =>
  status === STOCK_PHOTO_IMPORT_STATUS.QUEUED ||
  status === STOCK_PHOTO_IMPORT_STATUS.PROCESSING;

export const isTerminalBatchStatus = (status: StockPhotoImportBatchStatus) =>
  status === STOCK_PHOTO_IMPORT_STATUS.COMPLETED ||
  status === STOCK_PHOTO_IMPORT_STATUS.COMPLETED_WITH_ERRORS ||
  status === STOCK_PHOTO_IMPORT_STATUS.FAILED ||
  status === STOCK_PHOTO_IMPORT_STATUS.CANCELED;

const stockPhotoImportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOpenStockPhotoImportBatches: builder.query<StockPhotoImportBatch[], number | void>({
      query: () => ({
        url: `${stockPhotoImportBatchesPath()}?status=open`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: unwrapApiData<StockPhotoImportBatch[]>,
    }),
    getStockPhotoImportBatch: builder.query<StockPhotoImportBatch, string>({
      query: (batchId) => ({
        url: `${stockPhotoImportBatchesPath()}/${batchId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
    createAccountStockPhotoImport: builder.mutation<
      StockPhotoImportBatch,
      CreateAccountStockPhotoImportParams
    >({
      query: ({ accountId, idempotencyKey }) => ({
        url: `${ENDPOINT}/vehicles/import-stock-photos`,
        method: 'POST',
        body: { account: accountId },
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      }),
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
    createVehiclesStockPhotoImport: builder.mutation<
      StockPhotoImportBatch,
      CreateVehiclesStockPhotoImportParams
    >({
      query: ({ vehicleIds, idempotencyKey }) => ({
        url: `${ENDPOINT}/vehicles/bulk/import-stock-photos`,
        method: 'POST',
        body: { vehicles: vehicleIds },
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      }),
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
    cancelStockPhotoImportBatch: builder.mutation<StockPhotoImportBatch, string>({
      query: (batchId) => ({
        url: `${stockPhotoImportBatchesPath()}/${batchId}/cancel`,
        method: 'POST',
      }),
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
  }),
});

export const {
  useLazyGetOpenStockPhotoImportBatchesQuery,
  useLazyGetStockPhotoImportBatchQuery,
  useCreateAccountStockPhotoImportMutation,
  useCreateVehiclesStockPhotoImportMutation,
  useCancelStockPhotoImportBatchMutation,
} = stockPhotoImportsApi;

export default stockPhotoImportsApi;
